import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Container, Button, Form, Badge, Spinner } from 'react-bootstrap'
import Select from 'react-select'
import DatePicker from 'react-datepicker'
import "react-datepicker/dist/react-datepicker.css"
import ResizableTable from '../components/ResizableTable'
import PaginationComponent from '../components/Pagination'
import { amazonAPI } from '../api/amazonAPI'
import { formatDateOnly, formatDateTime } from '../utils/commonUtils'
import { formatErrorResponse } from '../utils/errorHandlers'
import { usePermissions } from '../contexts/PermissionContext'
import '../styles/selectControl.css'

const STATUS_BADGES = {
    SUBMITTED: 'info',
    PROCESSING: 'warning',
    DONE: 'success',
    FAILED: 'danger',
}

function AmazonSearchTermReportListScreen() {
    const { hasPermission } = usePermissions()
    const navigate = useNavigate()
    const location = useLocation()

    const urlParams = new URLSearchParams(location.state?.returnFilters || location.search)

    const [accounts, setAccounts] = useState([])
    const [selectedAccountId, setSelectedAccountId] = useState(() => urlParams.get('account') || '')
    const [error, setError] = useState('')

    const [startDate, setStartDate] = useState(null)
    const [endDate, setEndDate] = useState(null)
    const [submitting, setSubmitting] = useState(false)
    const [activeQuickDate, setActiveQuickDate] = useState(null)

    const [reports, setReports] = useState([])
    const [reportsLoading, setReportsLoading] = useState(false)
    const autoRefreshRef = useRef(null)

    const [currentPage, setCurrentPage] = useState(() => parseInt(urlParams.get('page')) || 1)
    const [totalItems, setTotalItems] = useState(0)
    const [pageSize, setPageSize] = useState(PaginationComponent.getCurrentPageSize())
    const [loadingReportId, setLoadingReportId] = useState(null)
    const [sourceFilter, setSourceFilter] = useState(() => urlParams.get('source') || '')

    useEffect(() => {
        if (location.state?.returnFilters) {
            navigate(`${location.pathname}${location.state.returnFilters}`, {
                replace: true, state: {}
            })
        }
    }, [])

    useEffect(() => {
        const loadAccounts = async () => {
            const data = await amazonAPI.accounts.getAll()
            if (data) {
                const active = data.filter(a => a.is_active)
                setAccounts(active)
                if (active.length === 1 && !selectedAccountId) {
                    setSelectedAccountId(String(active[0].id))
                }
            }
        }
        loadAccounts()
    }, [])

    useEffect(() => {
        fetchReports()
    }, [selectedAccountId, currentPage, pageSize, sourceFilter])

    useEffect(() => {
        const hasActive = reports.some(r => ['SUBMITTED', 'PROCESSING'].includes(r.status))
        if (autoRefreshRef.current) {
            clearInterval(autoRefreshRef.current)
            autoRefreshRef.current = null
        }
        if (hasActive) {
            autoRefreshRef.current = setInterval(fetchReports, 30000)
        }
        return () => {
            if (autoRefreshRef.current) clearInterval(autoRefreshRef.current)
        }
    }, [reports])

    useEffect(() => {
        updateUrlWithFilters()
    }, [selectedAccountId, currentPage, sourceFilter])

    const fetchReports = useCallback(async () => {
        setReportsLoading(true)
        try {
            const params = { page: currentPage, page_size: pageSize }
            if (selectedAccountId) params.account_id = selectedAccountId
            if (sourceFilter) params.collection_type = sourceFilter
            const data = await amazonAPI.searchTermReports.getAll(params)
            if (data) {
                setReports(data.results || [])
                setTotalItems(data.count || 0)
            }
        } catch (err) {
            setError(formatErrorResponse(err))
        } finally {
            setReportsLoading(false)
        }
    }, [selectedAccountId, currentPage, pageSize, sourceFilter])

    const updateUrlWithFilters = () => {
        const params = new URLSearchParams()
        if (selectedAccountId) params.set('account', selectedAccountId)
        if (currentPage > 1) params.set('page', currentPage)
        if (sourceFilter) params.set('source', sourceFilter)
        const search = params.toString()
        navigate(`${location.pathname}${search ? '?' + search : ''}`, { replace: true })
    }

    const getCurrentFilters = () => {
        const params = new URLSearchParams()
        if (selectedAccountId) params.set('account', selectedAccountId)
        if (currentPage > 1) params.set('page', currentPage)
        if (sourceFilter) params.set('source', sourceFilter)
        const search = params.toString()
        return search ? '?' + search : ''
    }

    const handleGenerate = async () => {
        if (!selectedAccountId || !startDate || !endDate) {
            setError('Please select an account, start date, and end date')
            return
        }
        setError('')
        setSubmitting(true)
        const start = startDate.toISOString().split('T')[0]
        const end = endDate.toISOString().split('T')[0]
        const data = await amazonAPI.searchTermReports.generate(selectedAccountId, start, end)
        if (data) {
            fetchReports()
        } else {
            setError('Failed to generate report')
        }
        setSubmitting(false)
    }

    const handleDeleteReport = async (row) => {
        if (!window.confirm('Delete this report and all its data?')) return
        await amazonAPI.searchTermReports.delete(row.id)
        fetchReports()
    }

    const handleCancelReport = async (row) => {
        setLoadingReportId(row.id)
        setError('')
        const result = await amazonAPI.searchTermReports.cancel(row.id)
        setLoadingReportId(null)
        if (result) fetchReports()
    }

    const handleRetryReport = async (row) => {
        setLoadingReportId(row.id)
        setError('')
        const result = await amazonAPI.searchTermReports.retry(row.id)
        setLoadingReportId(null)
        if (result) fetchReports()
    }

    const getStatusBadge = (status) => (
        <Badge bg={STATUS_BADGES[status] || 'secondary'}>{status}</Badge>
    )

    const accountOptions = accounts.map(a => ({ value: String(a.id), label: a.account_name }))

    const reportColumns = [
        { id: 'id', header: 'ID', cell: (row) => row.id },
        { id: 'account', header: 'Account', cell: (row) => row.seller_account_name || '' },
        { id: 'status', header: 'Status', cell: (row) => (
            <>
                {getStatusBadge(row.status)}
                {['SUBMITTED', 'PROCESSING'].includes(row.status) && (
                    <Spinner size="sm" className="ms-1" />
                )}
            </>
        )},
        { id: 'dateRange', header: 'Date Range', cell: (row) => {
            const start = row.start_date ? formatDateOnly(row.start_date) : ''
            const end = row.end_date ? formatDateOnly(row.end_date) : ''
            return start && end ? `${start} – ${end}` : ''
        }},
        { id: 'source', header: 'Source', cell: (row) => row.collection_type === 'daily'
            ? <Badge bg="info">Auto</Badge>
            : <Badge bg="secondary">Manual</Badge>
        },
        { id: 'rows', header: 'Rows', cell: (row) => row.row_count != null ? row.row_count.toLocaleString() : '' },
        { id: 'requestedAt', header: 'Requested', cell: (row) => formatDateTime(row.requested_at) },
        { id: 'actions', header: 'Actions', cell: (row) => loadingReportId === row.id ? (
            <Spinner size="sm" />
        ) : (
            <>
                {row.status === 'DONE' && (
                    <Button variant="primary" size="sm" className="me-2"
                        onClick={(e) => { e.stopPropagation(); navigate(`/amazon-search-term-report/${row.id}`, { state: { returnFilters: getCurrentFilters() } }) }}>
                        Details
                    </Button>
                )}
                {['SUBMITTED', 'PROCESSING'].includes(row.status) && hasPermission('amazon.manage') && (
                    <Button variant="outline-danger" size="sm" className="me-2"
                        onClick={(e) => { e.stopPropagation(); handleCancelReport(row) }}>
                        Cancel
                    </Button>
                )}
                {row.status === 'FAILED' && hasPermission('amazon.manage') && (
                    <Button variant="outline-warning" size="sm" className="me-2"
                        onClick={(e) => { e.stopPropagation(); handleRetryReport(row) }}>
                        Retry
                    </Button>
                )}
                {!['SUBMITTED', 'PROCESSING'].includes(row.status) && hasPermission('amazon.manage') && (
                    <Button variant="outline-danger" size="sm"
                        onClick={(e) => { e.stopPropagation(); handleDeleteReport(row) }}>Delete</Button>
                )}
            </>
        )}
    ]

    return (
        <Container fluid className="px-4 py-4">
            <h1 className="mb-3">Search Term Reports</h1>

            {error && <div className="alert alert-danger">{error}</div>}

            <div style={{ maxWidth: '900px' }}>
                <div className="d-flex align-items-end gap-3 flex-wrap mb-2">
                    <div style={{ minWidth: '220px', flex: 1 }}>
                        <Form.Label>Amazon Account</Form.Label>
                        <Select
                            value={accountOptions.find(opt => opt.value === selectedAccountId) || null}
                            onChange={(opt) => { setSelectedAccountId(opt?.value || ''); setCurrentPage(1) }}
                            options={accountOptions}
                            isClearable
                            placeholder="All Accounts..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                        />
                    </div>
                    <div style={{ width: '150px' }}>
                        <Form.Label>Start Date</Form.Label>
                        <DatePicker
                            selected={startDate}
                            onChange={(date) => { setStartDate(date); setActiveQuickDate(null) }}
                            className="form-control"
                            dateFormat="MM/dd/yyyy"
                            selectsStart startDate={startDate} endDate={endDate} maxDate={endDate}
                            showMonthDropdown showYearDropdown dropdownMode="select" portalId="root"
                        />
                    </div>
                    <div style={{ width: '150px' }}>
                        <Form.Label>End Date</Form.Label>
                        <DatePicker
                            selected={endDate}
                            onChange={(date) => { setEndDate(date); setActiveQuickDate(null) }}
                            className="form-control"
                            dateFormat="MM/dd/yyyy"
                            selectsEnd startDate={startDate} endDate={endDate} minDate={startDate}
                            maxDate={new Date()} openToDate={startDate}
                            showMonthDropdown showYearDropdown dropdownMode="select" portalId="root"
                        />
                    </div>
                    <div className="d-flex gap-1 align-items-center" style={{ paddingBottom: '4px' }}>
                        {[{ label: '1M', months: 1 }, { label: '3M', months: 3 }, { label: '6M', months: 6 }].map(({ label, months }) => (
                            <Badge key={months} bg={activeQuickDate === months ? 'primary' : 'light'}
                                style={{ cursor: 'pointer', fontSize: '0.8rem' }}
                                onClick={() => {
                                    const end = new Date(); const start = new Date()
                                    start.setMonth(end.getMonth() - months)
                                    setStartDate(start); setEndDate(end); setActiveQuickDate(months)
                                }}>{label}</Badge>
                        ))}
                    </div>
                    {hasPermission('amazon.manage') && (
                        <div>
                            <Button variant="success" onClick={handleGenerate}
                                disabled={submitting || !selectedAccountId || !startDate || !endDate}>
                                {submitting ? (<><Spinner size="sm" className="me-2" />Generating...</>) : 'Generate Report'}
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            <hr className="my-4" />

            <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="mb-0">Report Requests</h4>
                    <Button variant="outline-secondary" size="sm" onClick={fetchReports} disabled={reportsLoading}>
                        <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                    </Button>
                </div>

                <div className="d-flex mb-3">
                    <div className="d-flex gap-2 rounded p-2" style={{ background: 'var(--bs-tertiary-bg)' }}>
                        <Form.Group>
                            <Form.Label className="mb-1" style={{ fontSize: '0.8rem' }}>Source</Form.Label>
                            <Form.Select size="sm" value={sourceFilter}
                                onChange={(e) => { setSourceFilter(e.target.value); setCurrentPage(1) }}>
                                <option value="">All</option>
                                <option value="on_demand">Manual</option>
                                <option value="daily">Auto</option>
                            </Form.Select>
                        </Form.Group>
                    </div>
                </div>

                {reportsLoading && reports.length === 0 ? (
                    <div className="text-center py-4"><Spinner animation="border" /></div>
                ) : reports.length === 0 ? (
                    <p className="text-muted">No reports found.</p>
                ) : (
                    <>
                        <div className="table-responsive">
                            <ResizableTable columns={reportColumns} data={reports} tableId="amazon_search_term_reports"
                                initialColumnWidths={{ id: 60, dateRange: 220, status: 140, rows: 80, requestedAt: 180, actions: 280 }}
                                minHeight="auto" striped hover className="table-sm" />
                        </div>
                        <div className="mt-3">
                            <PaginationComponent
                                currentPage={currentPage}
                                totalItems={totalItems}
                                onPageChange={setCurrentPage}
                                onItemsPerPageChange={setPageSize}
                            />
                        </div>
                    </>
                )}
            </div>
        </Container>
    )
}

export default AmazonSearchTermReportListScreen
